import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { Suspense, useContext, useEffect, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { MaterialBranch, NewRequestBranch } from '../../models/dashboard-models';
import { ShortDevicePassport } from '../../models/warehouse-models';
import { resourceCache, UpdaterFn, useAsyncResource } from 'use-async-resource';
import { useGetAllShortOtherMaterialPassports, useGetReferencesForDistributor } from '../../utils/warehouses-hooks';
import { NoDataComponent } from '../NoDataComponent';
import { deleteMaterialFromBranch, useGetAllBranchMaterials } from '../../utils/clients-hooks';
import { InfoModal } from '../InfoModal';
import { CustomDropdown } from './CustomDropdown';
import { CustomSelect } from '../CustomSelect';
import { useGetUsers } from '../../utils/users-hooks';
import { AuthContext } from '../../context/AuthContext';
import { Role } from '../../utils/Enums/Roles';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../CustomToastContent';
import { toastOptionsTopRight } from '../../utils/Enums/ToastOptions';
import { PassportImages } from '../../pages/passports/PassportDetails';
import noImage from '../../assets/No_image_available.png';

export const NewRequestMaterialsTable = ({
                                             materials,
                                             onChange,
                                             isDisabled,
                                             assigneeId,
                                             isDashboard,
                                             selectedBranch,
                                             refetchMaterials,
                                         }: {
    materials: MaterialBranch[];
    onChange: (value: MaterialBranch[]) => void;
    isDisabled?: boolean;
    isDashboard?: boolean;
    assigneeId?: number;
    selectedBranch?: NewRequestBranch;
    refetchMaterials?: UpdaterFn<[id: number]>;
}) => {
    const [getOtherMaterials] = useAsyncResource(useGetAllShortOtherMaterialPassports, false, assigneeId);
    const otherMaterialsPassports = getOtherMaterials();
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { t: newRequestTranslation } = useTranslation('translation', { keyPrefix: 'newRequest' });

    const [material, setMaterial] = useState<ShortDevicePassport>();
    const [materialCount, setMaterialCount] = useState(1);
    const [forDelete, setForDelete] = useState(false);
    const [getDistributors] = useAsyncResource(useGetUsers, undefined, 'DISTRIBUTOR,INSTALLER');
    const distributors = getDistributors();
    const { hasPermission, loggedUser } = useContext(AuthContext);
    const isDistributor = !hasPermission([Role.COORDINATOR]) && ( hasPermission([Role.DISTRIBUTOR]) || hasPermission([Role.INSTALLER]) );

    useEffect(() => {
        if (isDistributor) {
            setDistributor(String(loggedUser?.id));
        } else if (selectedBranch?.assignedDistributor?.id) {
            setDistributor(String(selectedBranch.assignedDistributor?.id));
        }
    }, [forDelete]);

    const [distributor, setDistributor] = useState('');
    const [deleteReason, setDeleteReason] = useState('');
    const [freeTextReason, setFreeTextReason] = useState('');
    const [materialForDelete, setMaterialForDelete] = useState<MaterialBranch>();

    const addMaterial = (passport: ShortDevicePassport | undefined, count: number) => {
        if (passport?.id) {
            const { id: itemId, name } = passport;
            const model = undefined;

            const exists = materials.some(({ passportId: id }) => id === itemId);
            if (!exists) onChange([...materials, { id: 0, passportId: itemId, itemName: name, model, itemCount: count }]);
            else {
                onChange(
                    materials.map((item) => ( itemId == item.passportId ? { ...item, itemCount: item.itemCount + count } : item )),
                );
            }
        }
    };
    const removeMaterial = ({ id, passportId }: MaterialBranch) => {
        if (isDashboard) {
            setForDelete(true);

            materials.map((material) => material.id == id ? setMaterialForDelete(material) : null);
        } else {
            onChange(materials.filter(({ passportId: id }) => passportId != id));
        }
    };

    const deleteHandler = async (passportId: number) => {
        if (materialForDelete != undefined) {
            const response = await deleteMaterialFromBranch(
                materialForDelete.id, 1, distributor, deleteReason,
            );
            if (response) {
                resourceCache(useGetAllBranchMaterials).clear();
                if (refetchMaterials && selectedBranch?.id) {
                    refetchMaterials(selectedBranch.id);
                }
                toast.success(<CustomToastContent
                    content={[newRequestTranslation('success')]} />, toastOptionsTopRight);
            }
        }

        resourceCache(useGetReferencesForDistributor).clear();

        setFreeTextReason('');
        setDeleteReason('');
        setDistributor('');
        onChange(materials.flatMap((material) => {
            if (material.id !== materialForDelete?.id) return [material];
            return material.itemCount > 1 ? [{ ...material, itemCount: material.itemCount - 1 }] : [];
        }));
    };

    return !isDisabled || ( materials.length > 0 && isDisabled ) ? (
        <>
            <div className='w-100 d-flex flex-column'>
            {materials.length >= 0 &&
                materials.flatMap((material) => Array.from(
                    { length: material.itemCount },
                    (_, unitIndex) => ({ ...material, itemCount: 1, unitIndex })
                )).map((material, index) => {
                    const passport = otherMaterialsPassports.find(({ id }) => material.passportId === id);
                    const materialModel = passport?.model || passport?.name || material.itemName;
                    return (
                        <div key={`${material.id}-${material.unitIndex}-${index}`} className='w-100 border mt-1 d-flex'>
                            <div className='border-end device-img d-flex justify-content-center align-items-center'>
                                {material.passportId ? (
                                    <Suspense
                                        fallback={
                                            <div className='w-100 h-100 d-flex justify-content-center align-items-center'>
                                                <Spinner animation='border' />
                                            </div>
                                        }
                                    >
                                        <PassportImages id={material.passportId} />
                                    </Suspense>
                                ) : (
                                    <img className='passportImage noImg' src={noImage} alt='passport' />
                                )}
                            </div>
                            <div className='device-configuration d-flex align-items-center justify-content-between px-3 py-2'>
                                <div className='d-flex flex-column'>
                                    <Form.Label className='mb-1 fw-semibold'>
                                        {passport?.name ?? material.itemName}
                                    </Form.Label>
                                    {materialModel && (
                                        <span>
                                            {t('model')}: {materialModel}
                                        </span>
                                    )}
                                    {!isDashboard && <span>{t('quantity')}: {material.itemCount}</span>}
                                </div>
                                <Button
                                    type='button'
                                    size='sm'
                                    className='color-main px-1 mx-1 p-0'
                                    onClick={() => removeMaterial(material)}
                                    disabled={isDisabled && !isDashboard}
                                >
                                    <FontAwesomeIcon icon={faTrash} className='fa-fw' />
                                </Button>
                            </div>
                        </div>
                    );
                })}
            {!isDisabled && (
                <div className='w-100 border mt-1 d-flex'>
                    <div className='border-end device-img d-flex justify-content-center align-items-center'>
                        {material?.id ? (
                            <Suspense fallback={<Spinner animation='border' />}>
                                <PassportImages id={material.id} />
                            </Suspense>
                        ) : (
                            <img className='passportImage noImg' src={noImage} alt='passport' />
                        )}
                    </div>
                    <div className='device-configuration d-flex align-items-center gap-2 px-3 py-2'>
                        <div className='flex-grow-1'>
                        <Select
                            options={otherMaterialsPassports}
                            getOptionValue={(option) => `${option.id}`}
                            getOptionLabel={(option) => option.name}
                            onChange={(option) => option?.id && setMaterial(option)}
                            maxMenuHeight={150}
                            isDisabled={isDisabled}
                        />
                        </div>
                        <div style={{ width: '120px' }}>
                        <Form.Group controlId='formBasicEmail'>
                            <Form.Control
                                type='number'
                                placeholder={`${t('quantity')}`}
                                onChange={(e) => {
                                    setMaterialCount(+e.target.value);
                                }}
                                disabled={isDisabled}
                            />
                        </Form.Group>
                        </div>
                        <Button
                            disabled={!material}
                            type='button'
                            className='color-main p-1 m-0'
                            onClick={() => addMaterial(material, materialCount)}
                        >
                            <FontAwesomeIcon icon={faPlus} className='fa-fw' />
                        </Button>
                    </div>
                </div>
            )}
            </div>
            {forDelete && <InfoModal
                show={forDelete}
                isDisabled={distributor.length < 1 || deleteReason.length < 1}
                onHide={(isDelete: boolean) => {
                    if (isDelete && materialForDelete) {
                        deleteHandler(materialForDelete.passportId).then();
                    }
                    setFreeTextReason('');
                    setDeleteReason('');
                    return setForDelete(false);
                }}
            >
                <span className='w-100 text-secondary'>{`*${newRequestTranslation('fieldsRequired')}`}</span>

                {!isDistributor ? <CustomDropdown
                    entities={distributors.items.map((distributor) => {
                        return {
                            label: distributor.name,
                            value: String(distributor.id),
                        };
                    })}
                    label={t('distributor')}
                    setEntities={setDistributor}
                    defaultValue={selectedBranch?.assignedDistributor?.id}
                /> : <></>}
                <h5 className='w-100 mb-0 py-3'>{newRequestTranslation('reason')}</h5>
                <CustomSelect
                    customClass={'mb-3'}
                    updateValue={(value) => setDeleteReason(value)}
                    itemsToShow={['', 'damaged', 'defective', 'working', 'freetext']}
                />

                <Form.Control
                    placeholder={t('freetext')}
                    className={`${deleteReason !== 'freetext' ? 'disabled' : ''}`}
                    disabled={deleteReason !== 'freetext'}
                    name={'freetext'}
                    onChange={(e) => setFreeTextReason(e.currentTarget.value)}
                    value={freeTextReason}
                />
            </InfoModal>}
        </>

    ) : (
        <div className={'pb-3'}>
            <NoDataComponent noDataText={t('materials')} />
        </div>
    );
};
